import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import {Timestamp, doc, runTransaction} from 'firebase/firestore';
import {useCallback, useMemo, useState} from 'react';
import {FormattedDate} from 'react-intl';
import {useFirestore, useUser} from 'reactfire';
import {Loading} from '../common/Loading';
import {useDocument} from '../firebase/hooks/useDocument';
import {Translation} from '../i18n/Translation';
import {ComponentWithoutChildren} from '../react/types';
import {useWaitlistCollection} from './__hooks__/useWaitlistCollection';
import {SignupRule} from '#common/auth/rules';
import {WaitlistEntry} from '#common/auth/types';
import {SignupList} from '#common/firebase/collections';
import {EntityWithMetadata, MetadataField, ServerTimestampSentinelValue} from '#common/models/Entity';
import {hasPermission} from '#common/users/permissions';
import {User} from '#common/users/types';

export interface WaitlistListProps {
  user: User;
}

type Order = 'asc' | 'desc';

export const WaitlistList: ComponentWithoutChildren<WaitlistListProps> = ({user}: WaitlistListProps) => {
  const collection = useWaitlistCollection();
  const {data: entries, status} = collection.observe();
  const [orderBy, setOrderBy] = useState<keyof WaitlistEntry | 'createdTime'>('email');
  const [order, setOrder] = useState<Order>('asc');

  const canList = useMemo(() => hasPermission(user, 'list:signup.waitlist'), [user]);

  const handleRequestSort = (property: keyof WaitlistEntry | 'createdTime') => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedEntries = useMemo(() => {
    if (!entries) return [];
    return [...entries].sort((a, b) => {
      let valA: unknown = a[orderBy as keyof WaitlistEntry];
      let valB: unknown = b[orderBy as keyof WaitlistEntry];

      if (orderBy === 'createdTime') {
        valA = a[MetadataField]?.createdTime ?? 0;
        valB = b[MetadataField]?.createdTime ?? 0;
      }

      const aComp = (valA as number | string | Date | Timestamp) ?? '';
      const bComp = (valB as number | string | Date | Timestamp) ?? '';

      if (bComp < aComp) return order === 'asc' ? 1 : -1;
      if (bComp > aComp) return order === 'asc' ? -1 : 1;
      return 0;
    });
  }, [entries, order, orderBy]);

  if (status === 'loading') {
    return (
      <Box sx={{alignItems: 'center', display: 'flex', flexGrow: 1, justifyContent: 'center'}}>
        <Loading />
      </Box>
    );
  }

  if (!canList) {
    return null;
  }

  if (entries?.length === 0) {
    return (
      <Translation keys="user:administration.waitlist.empty">
        {(text) => (
          <Typography sx={{p: 4, textAlign: 'center'}} variant="body2">
            {text}
          </Typography>
        )}
      </Translation>
    );
  }

  return (
    <TableContainer sx={{flexGrow: 1}}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sortDirection={orderBy === 'email' ? order : false}>
              <TableSortLabel
                active={orderBy === 'email'}
                direction={orderBy === 'email' ? order : 'asc'}
                onClick={() => handleRequestSort('email')}
              >
                <Translation keys="user:administration.waitlist.column.email">{(text) => <>{text}</>}</Translation>
              </TableSortLabel>
            </TableCell>
            <TableCell sortDirection={orderBy === 'createdTime' ? order : false}>
              <TableSortLabel
                active={orderBy === 'createdTime'}
                direction={orderBy === 'createdTime' ? order : 'asc'}
                onClick={() => handleRequestSort('createdTime')}
              >
                <Translation keys="user:administration.waitlist.column.joined">{(text) => <>{text}</>}</Translation>
              </TableSortLabel>
            </TableCell>
            <TableCell align="right">
              <Translation keys="user:administration.waitlist.column.actions">{(text) => <>{text}</>}</Translation>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedEntries.map((entry) => (
            <WaitlistEntryItem key={entry.id} user={user} waitlistEntry={entry} />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

interface WaitlistEntryItemProps {
  user: User;
  waitlistEntry: WaitlistEntry;
}

const WaitlistEntryItem: ComponentWithoutChildren<WaitlistEntryItemProps> = ({
  user,
  waitlistEntry,
}: WaitlistEntryItemProps) => {
  const firestore = useFirestore();
  const {data: currentUser} = useUser();
  const document = useDocument<WaitlistEntry>(SignupList.waitlist, waitlistEntry.id);

  const canApprove = useMemo(
    () => hasPermission(user, 'update:signup.waitlist') && hasPermission(user, 'create:signup.rule'),
    [user],
  );
  const canDeny = useMemo(
    () => hasPermission(user, 'update:signup.waitlist') && hasPermission(user, 'create:signup.rule'),
    [user],
  );
  const canDelete = useMemo(() => hasPermission(user, 'delete:signup.waitlist'), [user]);

  const moveToList = useCallback(
    async (targetCollectionPath: string) => {
      const uid = currentUser?.uid;
      if (!uid) {
        return;
      }
      await runTransaction(firestore, (transaction) => {
        const waitlistRef = doc(firestore, SignupList.waitlist, waitlistEntry.id);
        const targetRef = doc(firestore, targetCollectionPath, waitlistEntry.id);

        const newRule: SignupRule = {
          description: `Approved from waitlist on ${new Date().toLocaleDateString()}`,
          enabled: true,
          id: waitlistEntry.id,
          type: 'exact',
          value: waitlistEntry.email,
        };

        transaction.set(targetRef, {
          ...newRule,
          [MetadataField]: {
            createdBy: uid,
            createdTime: ServerTimestampSentinelValue,
            updatedBy: uid,
            updatedTime: ServerTimestampSentinelValue,
          },
        });

        transaction.delete(waitlistRef);
        return Promise.resolve();
      });
    },
    [currentUser, firestore, waitlistEntry.email, waitlistEntry.id],
  );

  const approve = useCallback(() => {
    void moveToList(SignupList.whitelist);
  }, [moveToList]);

  const deny = useCallback(() => {
    void moveToList(SignupList.blacklist);
  }, [moveToList]);

  const remove = useCallback(() => {
    void document.delete();
  }, [document]);

  const createdDate = useMemo(() => {
    const time: unknown = (waitlistEntry as EntityWithMetadata<WaitlistEntry>)[MetadataField]?.createdTime;
    if (time instanceof Date) {
      return time;
    }
    if (time instanceof Timestamp) {
      return time.toDate();
    }
    if (typeof time === 'number' && time > 0) {
      return new Date(time);
    }
    return null;
  }, [waitlistEntry]);

  return (
    <TableRow hover>
      <TableCell>{waitlistEntry.email}</TableCell>
      <TableCell>
        {createdDate ? <FormattedDate dateStyle="short" timeStyle="short" value={createdDate} /> : '-'}
      </TableCell>
      <TableCell align="right">
        <Stack direction="row" spacing={1} sx={{justifyContent: 'flex-end'}}>
          {canApprove && (
            <Translation keys="user:administration.waitlist.action.approve">
              {(text) => (
                <Tooltip title={text}>
                  <IconButton color="success" onClick={approve} size="small">
                    <CheckIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Translation>
          )}
          {canDeny && (
            <Translation keys="user:administration.waitlist.action.deny">
              {(text) => (
                <Tooltip title={text}>
                  <IconButton color="error" onClick={deny} size="small">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Translation>
          )}
          {canDelete && (
            <Translation keys="user:administration.waitlist.action.remove">
              {(text) => (
                <Tooltip title={text}>
                  <IconButton onClick={remove} size="small">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Translation>
          )}
        </Stack>
      </TableCell>
    </TableRow>
  );
};
